/**
 * Admin shell — platform masthead + left nav + outlet.
 */

import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import { Masthead } from "../components/Masthead";
import { useToast } from "../components/Toast";

const NAV_ACTIVE = ({ isActive }: { isActive: boolean }) =>
  `admin-nav-link${isActive ? " is-active" : ""}`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      toast.error("Sign out failed", "Try again or refresh the page.");
    }
  };

  const roleBadge =
    user?.role === "admin"
      ? { label: "Admin", cls: "badge-admin" }
      : user?.role === "valuation_analyst"
      ? { label: "Analyst", cls: "badge-analyst" }
      : { label: user?.role ?? "", cls: "badge" };

  return (
    <div className="app-shell">
      <a href="#admin-main" className="skip-link">Skip to content</a>

      <header className="app-shell-header" role="banner">
        <Masthead variant="bar" as="div" linkTo="/admin" />
        <div className="app-shell-user">
          {user && (
            <>
              <span className="app-shell-user-email" title={user.email}>{user.email}</span>
              <span className={`badge ${roleBadge.cls}`}>{roleBadge.label}</span>
              <button className="btn btn-ghost btn-sm" onClick={handleLogout}>
                Sign out
              </button>
            </>
          )}
        </div>
      </header>
      <div className="brand-bar" aria-hidden="true"><span /><span /></div>

      <div className="admin-layout">
        <nav className="admin-nav" aria-label="Admin navigation">
          <div className="admin-nav-group">
            <div className="admin-nav-heading">Manage</div>
            <NavLink to="/admin" end className={NAV_ACTIVE}>Dashboard</NavLink>
            <NavLink to="/admin/clients" className={NAV_ACTIVE}>Clients</NavLink>
            <NavLink to="/admin/users" className={NAV_ACTIVE}>Users</NavLink>
          </div>
          <div className="admin-nav-group">
            <div className="admin-nav-heading">Coming soon</div>
            <span className="admin-nav-link is-disabled" aria-disabled="true">Properties</span>
            <span className="admin-nav-link is-disabled" aria-disabled="true">Records</span>
            <span className="admin-nav-link is-disabled" aria-disabled="true">Research</span>
            <span className="admin-nav-link is-disabled" aria-disabled="true">Settings</span>
          </div>
        </nav>

        <main id="admin-main" className="admin-content" tabIndex={-1}>
          <Outlet />
          <Colophon />
        </main>
      </div>
    </div>
  );
}

function Colophon() {
  return (
    <footer className="admin-colophon">
      <span className="sc">
        <strong className="text-ink">CRE Property KB</strong>
        <span aria-hidden="true"> &middot; </span>
        a division of Edge Realty Advisors
      </span>
      <span className="sc">
        This is not an appraisal.
      </span>
    </footer>
  );
}
